"use client";

import {JSX, MouseEvent, ReactNode, useRef, useState} from "react";

import {useSnapshot} from "valtio";

import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";
import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useTranslation} from "@/shared/i18n";
import {type SelectInstance, SelectOption} from "@/shared/models/form/select";
import {FloatingDropdown} from "@/shared/ui/overlay/floating-dropdown";

import {SelectFooter, SelectOption as SelectOptionComponent, SelectSearch, SelectTrigger} from "./parts";

import "./styles/select-field.scss";

interface SelectBaseProps<T = string, O extends SelectOption<T> = SelectOption<T>, M extends boolean = false> {
    model: SelectInstance<T, O, M>;
    label?: string;
    placeholder?: string;
    className?: string;
    maxHeight?: number; // Max height for dropdown in px
    icon?: ReactNode; // Optional icon for left side (FontAwesome, SVG, etc.)
    /**
     * If true, renders dropdown in a portal to avoid z-index issues.
     * Pass `portalZIndex` when the portal layer must stack above modals/drawers.
     */
    usePortal?: boolean;
    /** Forwarded to FloatingDropdown; only affects stacking when `usePortal` is true. */
    portalZIndex?: number;
}

export function SelectField<T = string, O extends SelectOption<T> = SelectOption<T>, M extends boolean = false>({
    model,
    label,
    placeholder = "Select...",
    className,
    maxHeight = 300,
    icon,
    usePortal = false,
    portalZIndex,
}: SelectBaseProps<T, O, M>): JSX.Element {
    const snapshot = useSnapshot(model);
    const ref = useScrollIntoView<HTMLButtonElement>(model);
    const [hasInteracted, setHasInteracted] = useState(false);
    const {t} = useTranslation(); // For option labels
    const {t: tCommon} = useTranslation("common");
    const {t: tValidation} = useTranslation("validation");

    // Ref for floating dropdown content (used for click-outside detection when usePortal=true)
    const floatingRef = useRef<HTMLDivElement | null>(null);

    // Close dropdown when clicking outside
    // When usePortal=true, dropdown is rendered in document.body via FloatingPortal,
    // so we need to ignore clicks on the portal content (otherwise touch events close dropdown on mobile)
    const dropdownRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: () => {
            if (snapshot.isOpen) {
                // Blur active element (search input) to close keyboard on mobile
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                model.close();
            }
        },
        enableSmartTouch: true, // Distinguish tap from scroll (for search input with keyboard)
        ignoredRefs: usePortal ? [floatingRef] : [],
    });

    const handleOptionClick = (option: unknown, e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const typedOption = option as O;
        if (typedOption.disabled) return;

        // Blur active element (search input) to prevent keyboard jump on mobile
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        model.toggleOption(typedOption);
    };

    const handleClearClick = (e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation();
        e.preventDefault();
        model.clear();
    };

    // Helper to get display label (translated if labelKey exists)
    const getOptionLabel = (opt: O): string => {
        return opt.labelKey ? t(opt.labelKey as Parameters<typeof t>[0], opt.labelParams) : opt.label;
    };

    const displayValue = (): string => {
        if (snapshot.selected.length === 0) {
            return placeholder;
        }

        if (snapshot.isMultiple) {
            return snapshot.selected.map((opt) => getOptionLabel(opt as O)).join(", ");
        }

        const selectedOpt = snapshot.selectedOption as O | undefined;
        return selectedOpt ? getOptionLabel(selectedOpt) : placeholder;
    };

    const onOpenChange = (open: boolean): void => {
        if (!open) {
            model.close();
            if (hasInteracted) {
                model.onBlur();
                setHasInteracted(false);
            }
        }
    };

    return (
        <div ref={dropdownRef} className={`select-field ${className || ""}`}>
            {label && (
                <label className="select-field__label">
                    {label} {snapshot.isRequired && <span className="select-field__required">*</span>}
                </label>
            )}

            <div className="select-field__wrapper">
                <FloatingDropdown
                    isOpen={snapshot.isOpen && !snapshot.disabled}
                    onOpenChange={onOpenChange}
                    placement="bottom-start"
                    offsetValue={4}
                    usePortal={usePortal}
                    portalZIndex={portalZIndex}
                    floatingRef={floatingRef}
                    trigger={
                        <SelectTrigger
                            ref={ref}
                            isOpen={snapshot.isOpen}
                            disabled={snapshot.disabled}
                            hasError={snapshot.shouldShowError}
                            displayValue={displayValue()}
                            icon={icon}
                            isLoading={snapshot.loading}
                            hasClearButton={snapshot.isClearableField && snapshot.selected.length > 0 && !snapshot.disabled}
                            onToggle={() => {
                                if (!hasInteracted) {
                                    setHasInteracted(true);
                                }
                                model.toggle();
                            }}
                            onClear={handleClearClick}
                        />
                    }
                    dropdownClassName="select-field__floating-dropdown"
                >
                    <div className="select-field__dropdown" style={{maxHeight: `${maxHeight}px`}}>
                        {snapshot.isSearchableField && (
                            <SelectSearch value={snapshot.searchQuery} onChange={(value) => model.setSearchQuery(value)} />
                        )}

                        <ul className="select-field__list">
                            {model.filteredOptions.length === 0 ? (
                                <li className="select-field__no-options">{tCommon("noOptionsAvailable")}</li>
                            ) : (
                                model.filteredOptions.map((option, index) => (
                                    <SelectOptionComponent
                                        key={index}
                                        option={option}
                                        isSelected={model.isSelected(option)}
                                        isMultiple={snapshot.isMultiple}
                                        onClick={handleOptionClick}
                                    />
                                ))
                            )}
                        </ul>

                        {snapshot.isMultiple && snapshot.selected.length > 0 && (
                            <SelectFooter
                                onSelectAll={(e) => {
                                    e.stopPropagation();
                                    model.selectAll();
                                }}
                                onClearAll={(e) => {
                                    e.stopPropagation();
                                    model.deselectAll();
                                }}
                            />
                        )}
                    </div>
                </FloatingDropdown>
            </div>

            {snapshot.shouldShowError && snapshot.error && (
                <span className="select-field__error">
                    {tValidation(snapshot.error.message as Parameters<typeof tValidation>[0], snapshot.error.messageParams)}
                </span>
            )}
        </div>
    );
}
